/**
 * @fileoverview Semana de atendimento em grupos de dias com o mesmo horário
 * (ou fechados). Usado no Horário de atendimento das Configurações e no horário
 * próprio da conexão (WhatsAppModal).
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import { makeStyles, Chip, IconButton, Typography, Tooltip, Switch } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import NumberFormat from "react-number-format";
import { Plus, Trash2 } from "lucide-react";

import ButtonWithSpinner from "../ButtonWithSpinner";
import UnsavedChangesHint from "../UnsavedChangesHint";
import { modernFieldStyles } from "../ModernSelect";
import { tokens } from "../Triage/styles";
import { useReportDirty } from "../../hooks/useLeaveGuard";
import { i18n } from "../../translate/i18n";

// `weekday` vai gravado com a semana (os horários salvos já têm esses nomes):
// é dado, não texto da tela. Na tela, o nome curto vem da tradução.
const ALL_DAYS = [
  { weekday: "Segunda-feira", weekdayEn: "monday" },
  { weekday: "Terça-feira", weekdayEn: "tuesday" },
  { weekday: "Quarta-feira", weekdayEn: "wednesday" },
  { weekday: "Quinta-feira", weekdayEn: "thursday" },
  { weekday: "Sexta-feira", weekdayEn: "friday" },
  { weekday: "Sábado", weekdayEn: "saturday" },
  { weekday: "Domingo", weekdayEn: "sunday" },
];

const t = (key) => i18n.t(`settings.schedules.form.${key}`);

const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];
const ALL_WEEKDAYS_EN = ALL_DAYS.map(d => d.weekdayEn);

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

const toMinutes = (hhmm) => {
  if (!TIME_RE.test(hhmm)) return null;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

/** Erro de um horário "HH:MM a HH:MM" (ou null). Os feriados usam a mesma regra. */
export const timeRangeError = (startTime, endTime) => {
  if (!startTime || !endTime) return t("errors.missingTime");
  const start = toMinutes(startTime);
  const end = toMinutes(endTime);
  if (start === null || end === null) return t("errors.invalidTime");
  if (start >= end) return t("errors.endBeforeStart");
  return null;
};

const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// Semana salva (7 dias) → grupos da tela: dias com o mesmo horário juntos e os
// fechados num grupo só. Lista vazia ("sem horário") → null.
const schedulesToGroups = (list) => {
  if (!Array.isArray(list) || list.length === 0) return null;
  const openMap = {};
  const closedDays = [];
  list.forEach((d) => {
    if (!d.startTime || !d.endTime) {
      if (d.weekdayEn) closedDays.push(d.weekdayEn);
      return;
    }
    const key = `${d.startTime}-${d.endTime}`;
    if (!openMap[key]) {
      openMap[key] = { id: makeId(), days: [], startTime: d.startTime, endTime: d.endTime, isClosed: false };
    }
    openMap[key].days.push(d.weekdayEn);
  });
  const groups = Object.values(openMap);
  if (closedDays.length > 0) {
    groups.push({ id: makeId(), days: closedDays, startTime: "", endTime: "", isClosed: true });
  }
  return groups;
};

// Grupos da tela → os 7 dias que vão para o servidor (dia sem grupo = fechado).
const groupsToSchedules = (groups) =>
  ALL_DAYS.map((d) => {
    const group = groups.find((g) => g.days.includes(d.weekdayEn));
    if (!group || group.isClosed) {
      return { weekday: d.weekday, weekdayEn: d.weekdayEn, startTime: "", endTime: "" };
    }
    return { weekday: d.weekday, weekdayEn: d.weekdayEn, startTime: group.startTime, endTime: group.endTime };
  });

// Chave de comparação pelo conteúdo (quem usa pode passar um array novo a cada
// render). Lista vazia ("sem horário", herda da empresa) é diferente de 7 dias
// fechados ("fechado a semana toda"): o servidor trata os dois de jeito diferente.
const NO_SCHEDULE_KEY = "none";
const schedulesKey = (list) => {
  if (!Array.isArray(list) || list.length === 0) return NO_SCHEDULE_KEY;
  return JSON.stringify(
    ALL_DAYS.map((d) => {
      const day = list.find((item) => item.weekdayEn === d.weekdayEn);
      return [d.weekdayEn, day?.startTime || "", day?.endTime || ""];
    })
  );
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const c = tokens(dark);
  return {
    root: { width: "100%", padding: theme.spacing(1) },
    groupCard: {
      borderRadius: 12,
      padding: theme.spacing(2, 2.5),
      marginBottom: theme.spacing(2),
      background: c.subtle,
      border: `1px solid ${c.border}`,
    },
    groupHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: theme.spacing(1),
      gap: theme.spacing(1),
    },
    closedToggle: { display: "flex", alignItems: "center", gap: theme.spacing(0.5) },
    closedLabel: { fontSize: "0.8rem", color: c.textSecondary, fontWeight: 500 },
    quickActions: { display: "flex", gap: theme.spacing(0.75), flexWrap: "wrap" },
    quickActionBtn: {
      cursor: "pointer",
      padding: "3px 10px",
      borderRadius: 8,
      fontSize: "0.72rem",
      fontWeight: 600,
      fontFamily: "inherit",
      border: `1px solid ${c.inputBorder}`,
      background: "transparent",
      color: c.textSecondary,
      transition: "background 120ms, color 120ms",
      "&:hover": { background: c.hover, color: c.textPrimary },
    },
    daysRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(0.75),
      marginBottom: theme.spacing(1.5),
    },
    dayChip: { fontWeight: 600, fontSize: "0.75rem", height: 30, cursor: "pointer", transition: "all 0.15s" },
    dayChipSelected: {
      background: `${c.fill} !important`,
      color: `${dark ? "#0f172a" : "#ffffff"} !important`,
      border: "none !important",
    },
    dayChipUnselected: {
      background: "transparent !important",
      color: `${c.textSecondary} !important`,
      border: `1px solid ${c.inputBorder} !important`,
    },
    // Dia que já está em outro grupo.
    dayChipDisabled: { opacity: "0.35 !important", cursor: "not-allowed !important", pointerEvents: "none" },
    timeRow: { display: "flex", alignItems: "flex-start", gap: theme.spacing(1.5) },
    timeField: { ...modernFieldStyles(theme), flex: 1 },
    separator: { color: c.textMuted, fontSize: "1.2rem", flexShrink: 0, marginTop: 12 },
    deleteBtn: {
      color: c.textMuted,
      marginTop: 4,
      "&:hover": { color: c.danger, background: c.hover },
    },
    closedNote: {
      flex: 1,
      padding: theme.spacing(1.2, 1.5),
      borderRadius: 8,
      border: `1px dashed ${c.inputBorder}`,
      color: c.textSecondary,
      fontSize: "0.85rem",
      fontWeight: 500,
      textAlign: "center",
    },
    addButton: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(1.2, 2.5),
      borderRadius: 10,
      border: `1px dashed ${c.inputBorder}`,
      color: c.textSecondary,
      cursor: "pointer",
      marginBottom: theme.spacing(2),
      background: "transparent",
      width: "100%",
      fontFamily: "inherit",
      transition: "background 120ms, color 120ms",
      "&:hover": { color: c.textPrimary, background: c.hover },
    },
    addButtonText: { fontSize: "0.875rem", fontWeight: 500 },
    emptyHint: {
      textAlign: "center",
      color: c.textMuted,
      fontSize: "0.85rem",
      marginBottom: theme.spacing(2),
      padding: theme.spacing(3),
      border: `1px dashed ${c.border}`,
      borderRadius: 12,
    },
    errorText: { color: c.danger, fontSize: "0.75rem", marginTop: theme.spacing(0.5) },
    buttonContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: theme.spacing(1.5),
    },
    saveButton: {
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 600,
      boxShadow: "none",
      "&:hover": { boxShadow: "none" },
    },
  };
});

// onDirtyChange(bool), opcional: avisa quando a semana na tela difere da salva.
function SchedulesForm({ initialValues, onSubmit, loading, labelSaveButton, onDirtyChange }) {
  const classes = useStyles();
  const initialKey = schedulesKey(initialValues);
  // A semana salva já entra no 1º render (sem piscar "nenhum horário" e sem
  // empurrar o que vem abaixo, como os feriados).
  const [groups, setGroups] = useState(() => schedulesToGroups(initialValues) || []);
  // Conteúdo salvo que já virou grupos: enquanto difere do atual, a tela ainda
  // não mostra o salvo e não pode contar como alteração.
  const [syncedKey, setSyncedKey] = useState(initialKey);
  const initialRef = useRef(initialValues);
  initialRef.current = initialValues;

  // O salvo mudou depois de montar: refaz os grupos. Só o CONTEÚDO conta: um
  // array novo com o mesmo conteúdo não apaga o que a pessoa está editando.
  useEffect(() => {
    if (initialKey === syncedKey) return;
    setSyncedKey(initialKey);
    const parsed = schedulesToGroups(initialRef.current);
    if (parsed) setGroups(parsed);
  }, [initialKey, syncedKey]);

  const usedDays = groups.flatMap(g => g.days);
  const availableDays = ALL_DAYS.filter(d => !usedDays.includes(d.weekdayEn));

  const handleToggleDay = (groupId, weekdayEn) => {
    setGroups(prev => prev.map(g => {
      if (g.id !== groupId) return g;
      const has = g.days.includes(weekdayEn);
      return { ...g, days: has ? g.days.filter(d => d !== weekdayEn) : [...g.days, weekdayEn] };
    }));
  };

  const handleTimeChange = (groupId, field, value) => {
    setGroups(prev => prev.map(g => g.id === groupId ? { ...g, [field]: value } : g));
  };

  const handleRemoveGroup = (groupId) => {
    setGroups(prev => prev.filter(g => g.id !== groupId));
  };

  const handleAddGroup = () => {
    setGroups(prev => [...prev, {
      id: makeId(),
      days: availableDays.map(d => d.weekdayEn),
      startTime: "",
      endTime: "",
      isClosed: false,
    }]);
  };

  const handleToggleClosed = (groupId) => {
    setGroups(prev => prev.map(g => {
      if (g.id !== groupId) return g;
      return { ...g, isClosed: !g.isClosed, startTime: "", endTime: "" };
    }));
  };

  // "Copiar para dias úteis": adiciona (sem remover de outros grupos) e
  // "rouba" dias já usados em outros grupos pra este.
  const handleApplyPreset = (groupId, daysPreset) => {
    setGroups(prev => prev.map(g => {
      if (g.id === groupId) return { ...g, days: daysPreset };
      return { ...g, days: g.days.filter(d => !daysPreset.includes(d)) };
    }));
  };

  // Validação por grupo: { [groupId]: "mensagem" | null }
  const groupErrors = useMemo(() => {
    const errors = {};
    groups.forEach(g => {
      const timeError = g.isClosed ? null : timeRangeError(g.startTime, g.endTime);
      errors[g.id] = timeError || (g.days.length === 0 ? t("errors.noDays") : null);
    });
    return errors;
  }, [groups]);

  const hasErrors = Object.values(groupErrors).some(Boolean);

  // Sem nenhum grupo na tela é "sem horário"; com grupos, os 7 dias que seriam salvos.
  const currentKey = groups.length ? schedulesKey(groupsToSchedules(groups)) : NO_SCHEDULE_KEY;
  const dirty = syncedKey === initialKey && currentKey !== initialKey;
  useReportDirty(dirty, onDirtyChange);

  const handleSubmit = () => {
    if (hasErrors) return;
    onSubmit(groupsToSchedules(groups));
  };

  return (
    <div className={classes.root}>
      {groups.length === 0 && (
        <div className={classes.emptyHint}>{t("empty")}</div>
      )}

      {groups.map((group) => {
        const otherUsedDays = groups.filter(g => g.id !== group.id).flatMap(g => g.days);
        const error = groupErrors[group.id];
        // Campo em vermelho só com os dois horários preenchidos (vazio não é erro de digitação).
        const hasTimeError =
          !group.isClosed && !!group.startTime && !!group.endTime && !!timeRangeError(group.startTime, group.endTime);

        return (
          <div key={group.id} className={classes.groupCard}>
            {/* Header: quick actions à esquerda + toggle fechado à direita */}
            <div className={classes.groupHeader}>
              <div className={classes.quickActions}>
                <button
                  type="button"
                  className={classes.quickActionBtn}
                  onClick={() => handleApplyPreset(group.id, WEEKDAYS)}
                  title={t("workdaysHint")}
                >
                  {t("workdays")}
                </button>
                <button
                  type="button"
                  className={classes.quickActionBtn}
                  onClick={() => handleApplyPreset(group.id, ALL_WEEKDAYS_EN)}
                  title={t("allDaysHint")}
                >
                  {t("allDays")}
                </button>
              </div>
              <div className={classes.closedToggle}>
                <Typography className={classes.closedLabel}>{t("closedDay")}</Typography>
                <Switch
                  size="small"
                  checked={group.isClosed}
                  onChange={() => handleToggleClosed(group.id)}
                  color="primary"
                />
              </div>
            </div>

            {/* Chips dos dias */}
            <div className={classes.daysRow}>
              {ALL_DAYS.map(d => {
                const isSelected = group.days.includes(d.weekdayEn);
                const isDisabled = otherUsedDays.includes(d.weekdayEn);
                return (
                  <Chip
                    key={d.weekdayEn}
                    label={t(`days.${d.weekdayEn}`)}
                    size="small"
                    className={`${classes.dayChip} ${
                      isDisabled
                        ? classes.dayChipDisabled
                        : isSelected
                          ? classes.dayChipSelected
                          : classes.dayChipUnselected
                    }`}
                    onClick={() => !isDisabled && handleToggleDay(group.id, d.weekdayEn)}
                  />
                );
              })}
            </div>

            {/* Campos de hora + botão remover, ou o aviso de fechado */}
            <div className={classes.timeRow}>
              {group.isClosed ? (
                <div className={classes.closedNote}>{t("closedNote")}</div>
              ) : (
                <>
                  <NumberFormat
                    label={t("initialHour")}
                    value={group.startTime}
                    onValueChange={({ formattedValue }) => handleTimeChange(group.id, "startTime", formattedValue)}
                    variant="outlined"
                    margin="dense"
                    customInput={TextField}
                    format="##:##"
                    error={hasTimeError}
                    className={classes.timeField}
                    placeholder="09:00"
                  />

                  <span className={classes.separator}>–</span>

                  <NumberFormat
                    label={t("finalHour")}
                    value={group.endTime}
                    onValueChange={({ formattedValue }) => handleTimeChange(group.id, "endTime", formattedValue)}
                    variant="outlined"
                    margin="dense"
                    customInput={TextField}
                    format="##:##"
                    error={hasTimeError}
                    className={classes.timeField}
                    placeholder="18:00"
                  />
                </>
              )}

              <Tooltip title={t("remove")}>
                <IconButton size="small" className={classes.deleteBtn} onClick={() => handleRemoveGroup(group.id)}>
                  <Trash2 size={18} />
                </IconButton>
              </Tooltip>
            </div>

            {error && <div className={classes.errorText}>{error}</div>}
          </div>
        );
      })}

      {/* Botão adicionar — só aparece quando há dias disponíveis */}
      {availableDays.length > 0 && (
        <button type="button" className={classes.addButton} onClick={handleAddGroup}>
          <Plus size={18} />
          <span className={classes.addButtonText}>{t("add")}</span>
        </button>
      )}

      <div className={classes.buttonContainer}>
        <UnsavedChangesHint show={dirty} />
        <ButtonWithSpinner
          loading={loading}
          type="button"
          color="primary"
          variant="contained"
          className={classes.saveButton}
          onClick={handleSubmit}
          disabled={hasErrors || !dirty}
        >
          {labelSaveButton ?? t("save")}
        </ButtonWithSpinner>
      </div>
    </div>
  );
}

export default SchedulesForm;
